import { getDensityMetrics, getDiscordPalette } from "@revolt/common";
import { SelectedTheme } from "@revolt/state/stores/Theme";

/**
 * Generate Stoat for Web variables
 * @param theme Theme
 * @returns CSS Variables
 */
export function createStoatWebVariables(theme: SelectedTheme) {
  const palette = getDiscordPalette(theme.baseTheme);
  const density = getDensityMetrics(theme.density);

  return {
    // helper variables
    "--unset-fg": "red",
    "--unset-bg": "linear-gradient(to right, red, blue)",

    // message size
    "--message-size": `${theme.messageSize}px`,
    "--message-group-spacing": `${theme.messageGroupSpacing}px`,
    "--navigation-row-height": `${density.navigationRowHeight}px`,

    // emoji size
    "--emoji-size": "1.4em",
    "--emoji-size-medium": "48px",
    "--emoji-size-large": "96px",

    // effects
    "--effects-blur-md": theme.blur ? "blur(20px)" : "unset",
    "--effects-invert-black": theme.darkMode ? "invert(100%)" : "invert(0%)",
    "--effects-invert-light": theme.darkMode ? "invert(0%)" : "invert(1000%)",

    // transitions
    "--transitions-fast": "120ms ease-out",
    "--transitions-medium": "180ms ease-out",

    // Discord surface system
    "--discord-bg-base": palette.base,
    "--discord-bg-sidebar": palette.sidebar,
    "--discord-bg-primary": palette.primary,
    "--discord-bg-elevated": palette.elevated,
    "--discord-bg-floating":
      theme.baseTheme === "light" ? "#ffffff" : "#111214",
    "--discord-bg-input": palette.input,
    "--discord-text": palette.text,
    "--discord-text-muted": palette.muted,
    "--discord-accent": palette.accent,
    "--discord-mention": palette.mention,
    "--discord-hover":
      "color-mix(in srgb, var(--discord-text) 7%, transparent)",
    "--discord-selected":
      "color-mix(in srgb, var(--discord-text) 12%, transparent)",
    "--discord-divider":
      "color-mix(in srgb, var(--discord-text) 9%, transparent)",
    "--discord-danger": "#da373c",
    "--discord-success": "#23a559",
    "--discord-link": "#00a8fc",
    "--discord-radius-control": "4px",
    "--discord-radius-panel": "8px",
    "--discord-radius-menu": "8px",
    "--discord-shadow-low": "0 2px 8px rgb(0 0 0 / 20%)",
    "--discord-shadow-high": "0 8px 24px rgb(0 0 0 / 32%)",
    "--discord-z-header": "20",
    "--discord-z-popover": "1000",
    "--discord-z-modal": "2000",

    // Bridge Material-based primitives onto the Discord surfaces.
    "--md-sys-color-surface": palette.primary,
    "--md-sys-color-surface-variant": palette.sidebar,
    "--md-sys-color-surface-container-lowest": palette.primary,
    "--md-sys-color-surface-container-low": palette.primary,
    "--md-sys-color-surface-container": palette.sidebar,
    "--md-sys-color-surface-container-high": palette.elevated,
    "--md-sys-color-surface-container-highest": palette.input,
    "--md-sys-color-on-surface": palette.text,
    "--md-sys-color-on-surface-variant": palette.muted,
    "--md-sys-color-outline": palette.muted,
    "--md-sys-color-outline-variant":
      "color-mix(in srgb, var(--discord-text) 12%, transparent)",
    "--md-sys-color-primary": palette.accent,
    "--md-sys-color-on-primary": "#ffffff",
    "--md-sys-color-primary-container":
      "color-mix(in srgb, var(--discord-accent) 28%, transparent)",
    "--md-sys-color-on-primary-container": palette.text,

    // brand
    "--brand-presence-online": "#23a559",
    "--brand-presence-idle": "#f0b232",
    "--brand-presence-busy": "#f23f43",
    "--brand-presence-focus": "#5865f2",
    "--brand-presence-invisible": "#80848e",

    // font
    "--fonts-primary": `"${theme.interfaceFont}", "Inter", sans-serif`,
    "--fonts-monospace": `"${theme.monospaceFont}", "Jetbrains Mono", sans-serif`,

    // load constants
    ...reduceWithPrefix(themeConstants.borderRadius, "--borderRadius-"),
    ...reduceWithPrefix(themeConstants.gap, "--gap-"),
    ...reduceWithPrefix(themeConstants.layout, "--layout-"),
  };
}

/**
 * Add prefix to all keys in an object
 * @param object Object
 * @param prefix Prefix
 * @returns New object
 */
function reduceWithPrefix(object: Record<string, string>, prefix: string) {
  return Object.entries(object).reduce(
    (d, [k, v]) => ({ ...d, [`${prefix}${k}`]: v }),
    {},
  );
}

const themeConstants = {
  borderRadius: {
    none: "0px",
    xs: "3px",
    sm: "4px",
    md: "6px",
    lg: "8px",
    li: "10px",
    xl: "12px",
    xli: "16px",
    xxl: "20px",
    full: "999px",
    circle: "100%",
  },
  /**
   * @deprecated decide this at a component level
   */
  gap: {
    none: "0",
    xxs: "1px",
    xs: "2px",
    s: "6px",
    sm: "4px",
    md: "8px",
    l: "12px",
    lg: "15px",
    x: "28px",
    xl: "32px",
    xxl: "64px",
  },
  layout: {
    "width-server-rail": "72px",
    "width-channel-sidebar": "304px",
    "width-member-sidebar": "264px",
    "height-header": "48px",
    "height-user-panel": "68px",
    "height-mobile-you-bar": "68px",
    "width-user-context-menu-truncate": "300px",
    "height-message-box": "32vh",
  },
};
